<template>
  <div id="Lgx02887687516358443008195" class="vr">
    <vx-card>
      <div class="vr-header">
        <div class="vr-parts">
          <svg class="vr-logo" viewBox="0 0 264.60476 117.61073" role="img" aria-label="Riot Games">
            <path :d="riotLogoPath" />
          </svg>
          <vs-button
            v-for="(part, index) in parts"
            :key="part.key"
            class="vr-part-button"
            :color="partColor(index)"
            :type="currentPart === index ? 'filled' : 'border'"
            @click="currentPart = index"
          >
            <font-awesome-icon :icon="['far', partIcon(index)]" class="vr-part-icon" />
            {{ part.label }}
          </vs-button>
        </div>
        <vs-button color="danger" type="border" class="vr-incident-button" @click="incidentOpen = true">
          <font-awesome-icon :icon="['far', 'triangle-exclamation']" class="vr-part-icon" />
          View INC-48213
        </vs-button>
      </div>
      <p class="vr-header-meta">
        Host <span class="vr-mono">replay03</span> · Volume group <span class="vr-mono">vg_replays</span> · Mount point
        <span class="vr-mono">/srv/replays</span>
      </p>
    </vx-card>

    <div class="vr-grid">
      <vx-card class="vr-diagnostics">
        <h4 class="vr-card-title">Diagnostics</h4>
        <p class="vr-card-subtitle">Select a command to run it and review its output.</p>
        <ul class="vr-command-list">
          <li v-for="item in parts[currentPart].commands" :key="item.cmd">
            <button type="button" class="vr-command" :class="{ 'is-ran': hasRun(item.cmd) }" @click="runCommand(item)">
              <span class="vr-command-prompt">#</span>
              <span class="vr-command-text">{{ item.cmd }}</span>
              <font-awesome-icon v-if="hasRun(item.cmd)" :icon="['far', 'check']" class="vr-command-check" />
            </button>
          </li>
        </ul>
      </vx-card>

      <div class="vr-work">
        <div class="vr-terminal">
          <div class="vr-terminal-bar">
            <span>root@replay03: ~</span>
            <button type="button" class="vr-terminal-clear" :disabled="!transcripts[currentPart].length" @click="clearTranscript">
              Clear
            </button>
          </div>
          <div ref="terminalBody" class="vr-terminal-body">
            <p v-if="!transcripts[currentPart].length" class="vr-terminal-hint">Run a diagnostic command to see its output here.</p>
            <div v-for="(entry, index) in transcripts[currentPart]" :key="index" class="vr-terminal-entry">
              <p class="vr-terminal-command">
                <span class="vr-terminal-prompt">{{ prompt }}</span>
                {{ entry.cmd }}
              </p>
              <pre v-if="entry.output" class="vr-terminal-output">{{ entry.output }}</pre>
            </div>
          </div>
        </div>

        <vx-card>
          <h4 class="vr-card-title">{{ parts[currentPart].question }}</h4>
          <div class="vr-answer" :class="resultClass(currentPart)">
            <span class="vr-answer-prompt">{{ prompt }}</span>
            <v-select
              v-model="selections[currentPart]"
              :options="parts[currentPart].options"
              :clearable="false"
              :searchable="false"
              placeholder="Select command"
              class="vr-select"
              @input="results.splice(currentPart, 1, null)"
            />
          </div>
          <p v-if="results[currentPart] === true" class="vr-result is-correct">Correct</p>
          <p v-else-if="results[currentPart] === false" class="vr-result is-wrong">Incorrect</p>
        </vx-card>
      </div>
    </div>

    <movable-popup
      refVal="vrIncidentPopup"
      title="INC-48213 · Click here to drag this window"
      width="38vw"
      padding="0"
      :active="incidentOpen"
      v-on:close="incidentOpen = false"
    >
      <template v-slot:content>
        <div class="vr-incident">
          <div class="vr-incident-row">
            <span>Severity</span>
            <span class="vr-incident-sev">Sev 2</span>
          </div>
          <div class="vr-incident-row">
            <span>Opened</span>
            <span>Mon Oct 12 2026 · 14:06</span>
          </div>
          <div class="vr-incident-row">
            <span>Reported by</span>
            <span>Jordan Reyes, Junior Linux Administrator</span>
          </div>
          <div class="vr-incident-row">
            <span>Affected host</span>
            <span class="vr-mono">replay03</span>
          </div>
          <div class="vr-incident-message">
            <p>
              I was cleaning up old test volumes on replay03 and removed <span class="vr-mono">lv_matches</span> by mistake. It holds the
              ranked match replays the esports review team pulls every morning.
            </p>
            <p>
              I unmounted <span class="vr-mono">/srv/replays</span> and ran <span class="vr-mono">lvremove</span> at 13:58. Nothing has
              been written to the disk since. Can you bring the volume back and make the replays available again?
            </p>
          </div>
        </div>
      </template>
    </movable-popup>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import MovablePopup from '../../../../../../../../components/popup/MovablePopup';

const RIOT_LOGO_PATH =
  'm 26.352344,87.796309 v 9.003522 h 11.099348 c 0.0011,1.826027 -0.470378,3.621289 -1.368711,5.211059 -0.939216,1.58388 -2.329004,2.85208 -3.992053,3.64274 -1.988489,0.90849 -4.159269,1.34754 -6.34452,1.28318 -2.523286,0.07 -5.008586,-0.62599 -7.128677,-1.99605 -2.070695,-1.43742 -3.668388,-3.45687 -4.590875,-5.802726 -1.150124,-2.933393 -1.700372,-6.067646 -1.618203,-9.217377 -0.08416,-3.125412 0.478922,-6.234457 1.653848,-9.131841 0.960587,-2.309051 2.570127,-4.290025 4.633651,-5.702941 2.080516,-1.350739 4.520541,-2.041501 7.000356,-1.981774 1.291089,-0.01125 2.577345,0.159068 3.820966,0.506136 1.106366,0.31691 2.150341,0.820819 3.086717,1.489901 0.889162,0.640678 1.656842,1.434902 2.266924,2.345326 0.637901,0.973965 1.118991,2.042004 1.425727,3.165136 h 12.311226 c -0.322221,-2.583492 -1.161047,-5.075724 -2.466523,-7.328277 -1.298975,-2.2224 -3.003756,-4.181089 -5.025715,-5.774229 -2.118897,-1.656331 -4.509214,-2.932295 -7.064523,-3.771068 -2.775323,-0.90332 -5.678654,-1.351098 -8.597176,-1.325937 -3.414838,-0.02844 -6.803218,0.600834 -9.980148,1.853462 -3.052539,1.215506 -5.8126274,3.064474 -8.0981782,5.424917 -2.3915532,2.494387 -4.2388818,5.45837 -5.4249138,8.704115 -1.35452126,3.756051 -2.01398257,7.727299 -1.94614171,11.719547 0,5.674422 1.07881261,10.56707 3.23643021,14.67794 2.0401818,3.95957 5.1817769,7.24475 9.0462845,9.45976 7.940458,4.23648 17.418962,4.45863 25.549171,0.5988 3.469032,-1.76002 6.357341,-4.48251 8.319172,-7.84156 2.019791,-3.417 3.027309,-7.5041 3.022558,-12.261301 V 87.789172 Z M 179.64025,116.88843 V 63.109702 h 36.83387 v 9.951628 h -24.67947 v 11.947666 h 19.95315 v 9.951628 h -19.95315 v 11.976186 h 24.7365 v 9.95162 z m 72.5913,-37.995839 c -0.10964,-1.870629 -1.03148,-3.599858 -2.52355,-4.733445 -1.48989,-1.133462 -3.56433,-1.696624 -6.19481,-1.696624 -1.51947,-0.05072 -3.03452,0.191299 -4.46256,0.712868 -1.07715,0.392931 -2.0229,1.079397 -2.73027,1.981774 -0.61279,0.824672 -0.94509,1.824058 -0.94813,2.85147 -0.0334,0.81887 0.16163,1.630893 0.56318,2.34534 0.42284,0.710288 1.00945,1.309126 1.71087,1.746525 0.82272,0.533351 1.70878,0.962004 2.63761,1.276027 1.08287,0.377281 2.18742,0.689131 3.3077,0.933857 l 4.72632,1.097818 c 2.31656,0.492688 4.57462,1.228652 6.7366,2.195621 1.87527,0.836035 3.60732,1.961864 5.13264,3.336236 1.39654,1.271913 2.50908,2.824092 3.26494,4.555217 0.79118,1.868088 1.18217,3.881229 1.14771,5.909685 0.08,3.07039 -0.80235,6.08894 -2.52355,8.63282 -1.81937,2.52636 -4.33291,4.47035 -7.2356,5.59601 -3.61878,1.39738 -7.47802,2.06603 -11.35599,1.9675 -3.93574,0.0841 -7.85043,-0.59616 -11.52705,-2.00314 -3.09615,-1.20268 -5.75884,-3.309 -7.64195,-6.04513 -1.82019,-2.68988 -2.77068,-6.0451 -2.85146,-10.065684 h 11.71241 c 0.031,3.179744 2.03508,6.005254 5.02572,7.085904 1.6473,0.62601 3.39921,0.93089 5.16115,0.89822 1.61689,0.0353 3.2264,-0.22816 4.74771,-0.77703 1.20154,-0.42419 2.26743,-1.16269 3.08672,-2.13861 0.72236,-0.90797 1.11675,-2.03338 1.11919,-3.19365 0.011,-1.04039 -0.3477,-2.050907 -1.01226,-2.851455 -0.81444,-0.888343 -1.82502,-1.574264 -2.95128,-2.003161 -1.59051,-0.648217 -3.23378,-1.158612 -4.91166,-1.525548 l -5.75285,-1.390086 c -4.5861,-1.093059 -8.20511,-2.818203 -10.85696,-5.175414 -2.65187,-2.35721 -3.96829,-5.550866 -3.94929,-9.580947 -0.0838,-3.09236 0.84772,-6.127041 2.65186,-8.639955 1.88897,-2.555346 4.43327,-4.55285 7.36393,-5.781356 3.37956,-1.43321 7.02268,-2.139972 10.693,-2.074441 3.65331,-0.08189 7.28123,0.625704 10.63599,2.074441 2.84867,1.239599 5.29518,3.249127 7.06452,5.802739 1.70045,2.558435 2.5944,5.568094 2.56633,8.639954 z M 170.97178,116.88843 H 158.9457 V 76.005477 l -1.23325,-0.01421 -12.14727,40.897213 h -12.01894 l -11.81935,-40.897213 -1.53265,0.01421 V 116.88843 H 108.16815 V 63.109702 h 20.43792 l 10.32232,36.042592 h 1.24751 l 10.32233,-36.042592 h 20.47355 z m -81.737385,0 H 102.25847 L 84.201547,63.116828 H 67.698662 L 49.613207,116.88843 H 62.658686 L 66.173131,105.0976 H 85.71995 Z M 68.967562,95.716268 74.82734,76.005477 h 2.209883 l 5.88829,19.710791 z M 52.607254,0.73379224 H 64.761648 V 54.512519 H 52.607254 Z M 122.48253,27.623154 c 0,5.902545 -1.1192,10.892617 -3.35761,14.970219 -2.07935,3.93663 -5.24349,7.194775 -9.11757,9.388465 -8.08852,4.315225 -17.795696,4.315225 -25.884213,0 -3.875102,-2.201999 -7.038911,-5.467224 -9.117582,-9.409849 -2.238406,-4.115624 -3.357606,-9.105697 -3.357606,-14.97022 0,-5.864525 1.1192,-10.866478 3.357606,-15.005862 2.074948,-3.924846 5.228499,-7.1748507 9.089064,-9.3670798 8.091045,-4.305103 17.793171,-4.305103 25.884211,0 3.87409,2.1936912 7.03822,5.4518364 9.11758,9.3884658 2.23841,4.106116 3.35761,9.108072 3.35761,15.005861 m -12.36825,0 c 0.0923,-3.16412 -0.44812,-6.314822 -1.5897,-9.267278 -0.90326,-2.307589 -2.4744,-4.293243 -4.51245,-5.702939 -4.252161,-2.613945 -9.613111,-2.613945 -13.865274,0 -2.040349,1.410781 -3.615833,3.395646 -4.526712,5.702939 -2.143931,5.992191 -2.143931,12.542367 0,18.534558 0.907862,2.309035 2.483986,4.294703 4.526712,5.702939 4.252163,2.613944 9.613113,2.613944 13.865274,0 2.04045,-1.407127 3.61224,-3.393598 4.51245,-5.702939 1.14065,-2.952713 1.68109,-6.103228 1.5897,-9.26728 m -83.020557,8.140949 6.244714,18.748416 H 46.647682 L 39.70436,34.316981 H 30.273115 L 29.70995,32.577585 c 1.796425,-0.349305 4.868883,-0.891086 7.592038,-2.74454 3.871802,-2.633943 6.203334,-7.001237 6.237592,-11.6839 0.0817,-3.186404 -0.722823,-6.332929 -2.32395,-9.08906 C 39.650881,6.3912518 37.328479,4.2474929 34.543194,2.9009091 31.277257,1.3885444 27.704361,0.65639571 24.10681,0.76230618 H 2.414244 V 54.512519 H 14.575765 V 35.764103 Z M 26.837092,24.636239 c -1.65549,0.60871 -3.41237,0.894263 -5.17542,0.841185 H 14.575765 V 10.607007 h 7.064523 c 1.764318,-0.05078 3.52113,0.249694 5.168286,0.883955 1.302555,0.518061 2.40278,1.443246 3.13662,2.637611 0.748273,1.204057 1.120574,2.603872 1.0693,4.020572 0.05808,1.395323 -0.30992,2.775274 -1.055039,3.956415 -0.737466,1.161038 -1.833783,2.049603 -3.122363,2.530679 M 123.63024,0.73379224 V 10.713937 h 20.88704 l 0.57028,1.746525 c -1.7679,0.342177 -4.75483,0.869699 -7.4352,2.637612 v 39.428702 h 12.02607 V 10.685423 h 13.98646 V 0.70527674 Z';

const DF_OUTPUT = `Filesystem      Size  Used Avail Use% Mounted on
devtmpfs        3.8G     0  3.8G   0% /dev
tmpfs           3.8G     0  3.8G   0% /dev/shm
/dev/nvme0n1p1   40G  6.2G   34G  16% /
tmpfs           777M     0  777M   0% /run/user/0`;

export default {
  data() {
    return {
      dataLocal: JSON.parse(JSON.stringify(this.data)),
      prompt: '[root@replay03 ~]#',
      riotLogoPath: RIOT_LOGO_PATH,
      currentPart: 0,
      incidentOpen: false,
      selections: [null, null, null],
      results: [null, null, null],
      transcripts: [[], [], []],
      parts: [
        {
          key: 'restore',
          label: 'Part 1',
          question: 'Select the appropriate command to begin the recovery process.',
          answer: 'vgcfgrestore vg_replays -f /etc/lvm/archive/vg_replays_00002-2093361175.vg',
          options: [
            'lvchange -a n /dev/vg_replays/lv_matches',
            'lvchange -a y /dev/vg_replays/lv_matches',
            'vgcfgrestore vg_replays -f /etc/lvm/archive/vg_replays_00002-2093361175.vg',
            'pvscan',
            'lvconvert --type mirror vg_replays/lv_matches',
            'vgcfgrestore vg_replays -f /etc/lvm/backup/vg_replays',
            'vgcfgrestore vg_replays -f /etc/lvm/archive/vg_replays_00001-1187742204.vg',
            'vgcfgrestore -t vg_replays -f /etc/lvm/archive/vg_replays_00002-2093361175.vg',
          ],
          commands: [
            { cmd: 'df -h', output: DF_OUTPUT },
            {
              cmd: 'history | tail -5',
              output: `  411  lvs
  412  df -h /srv/replays
  413  umount /srv/replays
  414  lvremove -y vg_replays/lv_matches
  415  history | tail -5`,
            },
            {
              cmd: 'vgs',
              output: `  VG         #PV #LV #SN Attr   VSize    VFree
  vg_replays   1   0   0 wz--n- <500.00g <500.00g`,
            },
            {
              cmd: 'pvs',
              output: `  PV           VG         Fmt  Attr PSize    PFree
  /dev/nvme1n1 vg_replays lvm2 a--  <500.00g <500.00g`,
            },
            { cmd: 'lvs', output: '' },
            {
              cmd: 'ls -l /etc/lvm/archive',
              output: `total 8
-rw-------. 1 root root 1342 Sep 30 09:12 vg_replays_00001-1187742204.vg
-rw-------. 1 root root 1871 Oct 12 13:58 vg_replays_00002-2093361175.vg`,
            },
            {
              cmd: 'vgcfgrestore --list vg_replays',
              output: `  File:         /etc/lvm/archive/vg_replays_00001-1187742204.vg
  VG name:      vg_replays
  Description:  Created *before* executing 'lvcreate -n lv_matches -L 480G vg_replays'
  Backup Time:  Wed Sep 30 09:12:41 2026

  File:         /etc/lvm/archive/vg_replays_00002-2093361175.vg
  VG name:      vg_replays
  Description:  Created *before* executing 'lvremove -y vg_replays/lv_matches'
  Backup Time:  Mon Oct 12 13:58:07 2026

  File:         /etc/lvm/backup/vg_replays
  VG name:      vg_replays
  Description:  Created *after* executing 'lvremove -y vg_replays/lv_matches'
  Backup Time:  Mon Oct 12 13:58:07 2026`,
            },
            {
              cmd: 'vgdisplay vg_replays',
              output: `  --- Volume group ---
  VG Name               vg_replays
  Format                lvm2
  VG Status             resizable
  Cur LV                0
  Open LV               0
  Cur PV                1
  VG Size               <500.00 GiB
  Free  PE / Size       127999 / <500.00 GiB`,
            },
          ],
        },
        {
          key: 'activate',
          label: 'Part 2',
          question: 'Select the appropriate command to continue the recovery process.',
          answer: 'lvchange -a y /dev/vg_replays/lv_matches',
          options: [
            'mount /dev/vg_replays/lv_matches /srv/replays',
            'pvchange -x y /dev/nvme1n1',
            'lvchange -a n /dev/vg_replays/lv_matches',
            'lvchange -a y /dev/vg_replays/lv_matches',
            'lvextend -L +20G vg_replays/lv_matches /dev/nvme1n1',
            'vgchange -a n vg_replays',
          ],
          commands: [
            {
              cmd: 'blkid',
              output: `/dev/nvme0n1p1: UUID="5c1e2a7d-93b4-4f0e-9a61-0d2c7f4b8e13" TYPE="xfs"
/dev/nvme1n1: UUID="Qm3vXa-7KpL-2dRt-9YwE-u8Hc-4Nf1-ZsB6Jk" TYPE="LVM2_member"`,
            },
            {
              cmd: 'vgs',
              output: `  VG         #PV #LV #SN Attr   VSize    VFree
  vg_replays   1   1   0 wz--n- <500.00g <20.00g`,
            },
            {
              cmd: 'lvs',
              output: `  LV         VG         Attr       LSize
  lv_matches vg_replays -wi------- 480.00g`,
            },
            {
              cmd: 'lvscan',
              output: '  inactive          \'/dev/vg_replays/lv_matches\' [480.00 GiB] inherit',
            },
            {
              cmd: 'lvdisplay /dev/vg_replays/lv_matches',
              output: `  --- Logical volume ---
  LV Path                /dev/vg_replays/lv_matches
  LV Name                lv_matches
  VG Name                vg_replays
  LV Write Access        read/write
  LV Status              NOT available
  LV Size                480.00 GiB`,
            },
            {
              cmd: 'ls -l /dev/vg_replays',
              output: 'ls: cannot access \'/dev/vg_replays\': No such file or directory',
            },
            {
              cmd: 'pvs',
              output: `  PV           VG         Fmt  Attr PSize    PFree
  /dev/nvme1n1 vg_replays lvm2 a--  <500.00g <20.00g`,
            },
          ],
        },
        {
          key: 'mount',
          label: 'Part 3',
          question: 'Select the appropriate command to complete the recovery process and access the underlying data.',
          answer: 'mount -a',
          options: [
            'mount /srv/replays /dev/vg_replays/lv_matches',
            'xfs_repair /dev/vg_replays/lv_matches',
            'mount -a',
            'xfs_mdrestore /dev/vg_replays /srv/replays',
            'lvscan -a',
            'mkfs.xfs /dev/vg_replays/lv_matches',
          ],
          commands: [
            {
              cmd: 'lsblk',
              output: `NAME                    MAJ:MIN RM  SIZE RO TYPE MOUNTPOINT
nvme0n1                 259:0    0   40G  0 disk
└─nvme0n1p1             259:1    0   40G  0 part /
nvme1n1                 259:2    0  500G  0 disk
└─vg_replays-lv_matches 253:0    0  480G  0 lvm`,
            },
            {
              cmd: 'lvscan',
              output: '  ACTIVE            \'/dev/vg_replays/lv_matches\' [480.00 GiB] inherit',
            },
            {
              cmd: 'cat /etc/fstab',
              output: `UUID=5c1e2a7d-93b4-4f0e-9a61-0d2c7f4b8e13  /             xfs  defaults         0 0
/dev/mapper/vg_replays-lv_matches          /srv/replays  xfs  defaults,nofail  0 0`,
            },
            {
              cmd: 'ls -l /dev/mapper/',
              output: `total 0
crw-------. 1 root root 10, 236 Oct 12 14:21 control
lrwxrwxrwx. 1 root root       7 Oct 12 14:21 vg_replays-lv_matches -> ../dm-0`,
            },
            { cmd: 'ls -l /srv/replays', output: 'total 0' },
            { cmd: 'df -h', output: DF_OUTPUT },
            {
              cmd: 'xfs_repair -n /dev/vg_replays/lv_matches',
              output: `Phase 1 - find and verify superblock...
Phase 2 - using internal log
        - zero log...
        - scan filesystem freespace and inode maps...
Phase 3 - for each AG...
Phase 4 - check for duplicate blocks...
Phase 5 - skipped (no modify flag)
Phase 6 - check inode connectivity...
Phase 7 - verify link counts...
No modify flag set, skipping filesystem flush and exiting.`,
            },
          ],
        },
      ],
    };
  },
  methods: {
    hasRun(cmd) {
      return this.transcripts[this.currentPart].some((entry) => entry.cmd === cmd);
    },
    runCommand(item) {
      this.transcripts[this.currentPart].push({ cmd: item.cmd, output: item.output });
      this.$nextTick(() => {
        const body = this.$refs.terminalBody;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
    clearTranscript() {
      this.transcripts.splice(this.currentPart, 1, []);
    },
    partIcon(index) {
      if (this.results[index] === true) return 'circle-check';
      if (this.results[index] === false) return 'circle-xmark';
      return this.selections[index] ? 'circle-dot' : 'circle';
    },
    partColor(index) {
      if (this.results[index] === true) return 'success';
      if (this.results[index] === false) return 'danger';
      return this.currentPart === index ? 'primary' : '#7a8597';
    },
    resultClass(index) {
      return { 'is-correct': this.results[index] === true, 'is-wrong': this.results[index] === false };
    },
    markSimulation() {
      let totalScore = 0;

      this.parts.forEach((part, index) => {
        const correct = this.selections[index] === part.answer;
        this.results.splice(index, 1, correct);
        if (correct) totalScore++;
      });

      if (totalScore === this.parts.length) {
        return this.$emit('simulation_full_correct', totalScore);
      }

      this.$emit('simulation_getting_there', totalScore);
    },
  },
  watch: {
    currentPart() {
      this.$nextTick(() => {
        const body = this.$refs.terminalBody;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
  },
  components: {
    vSelect,
    MovablePopup,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
#Lgx02887687516358443008195 {
  --vr-mono: 'Fira Code', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  --vr-line: #181d2a;
  --vr-line-strong: #262f42;
  --vr-text: #e2e7ee;
  --vr-muted: #9aa5b5;
  --vr-prompt: #f5c542;
  --vr-blue: #0053ff;
  --vr-green: #31b952;
  --vr-red: #e5484d;

  width: 100%;
  min-width: 0;
  font-size: 1.02rem;

  > * + * {
    margin-top: 1.5rem;
  }
}

.vr-mono {
  font-family: var(--vr-mono);
}

.vr-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.vr-parts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
}

.vr-part-button,
.vr-incident-button {
  font-weight: 600;
}

.vr-part-icon {
  margin-right: 0.4rem;
}

.vr-header-meta {
  margin: 1rem 0 0;
  color: var(--vr-muted);
}

.vr-logo {
  flex-shrink: 0;
  width: auto;
  height: 2.4rem;
  margin-right: 0.5rem;
  fill: #eb0029;
}

.vr-header-meta .vr-mono {
  color: var(--vr-text);
}

.vr-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 1.5rem;
  align-items: start;
}

.vr-card-title {
  margin: 0;
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.4;
}

.vr-card-subtitle {
  margin: 0.35rem 0 0;
  color: var(--vr-muted);
}

.vr-command-list {
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;

  li + li {
    margin-top: 0.45rem;
  }
}

.vr-command {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--vr-line);
  border-radius: 0.4rem;
  background: #070b16;
  color: var(--vr-text);
  font-family: var(--vr-mono);
  font-size: 0.95rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: var(--vr-blue);
  }

  &.is-ran {
    border-color: var(--vr-line-strong);
    color: var(--vr-muted);
  }
}

.vr-command-prompt {
  color: var(--vr-prompt);
}

.vr-command-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.vr-command-check {
  flex-shrink: 0;
  color: var(--vr-prompt);
}

.vr-work {
  min-width: 0;

  > * + * {
    margin-top: 1.5rem;
  }
}

.vr-terminal {
  overflow: hidden;
  border: 1px solid var(--vr-line);
  border-radius: 0.5rem;
  background: #111111;
}

.vr-terminal-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--vr-line);
  background: #161616;
  color: var(--vr-muted);
  font-family: var(--vr-mono);
  font-size: 0.85rem;
}

.vr-terminal-clear {
  padding: 0.15rem 0.6rem;
  border: 1px solid var(--vr-line-strong);
  border-radius: 0.3rem;
  background: transparent;
  color: var(--vr-text);
  font: inherit;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.vr-terminal-body {
  height: 22rem;
  padding: 1rem;
  overflow: auto;
  font-family: var(--vr-mono);
  font-size: 0.95rem;
  line-height: 1.5;
}

.vr-terminal-hint {
  margin: 0;
  color: var(--vr-muted);
}

.vr-terminal-entry + .vr-terminal-entry {
  margin-top: 0.85rem;
}

.vr-terminal-command {
  margin: 0;
  color: var(--vr-text);
}

.vr-terminal-prompt {
  color: var(--vr-prompt);
}

.vr-terminal-output {
  margin: 0.2rem 0 0;
  padding: 0;
  background: transparent;
  color: #c9d1d9;
  font-family: var(--vr-mono);
  font-size: inherit;
  line-height: inherit;
  white-space: pre;
}

.vr-answer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.vr-answer-prompt {
  flex-shrink: 0;
  color: var(--vr-prompt);
  font-family: var(--vr-mono);
  font-size: 1rem;
}

.vr-select {
  flex: 1 1 22rem;
  min-width: 0;
}

.vr-select ::v-deep .vs__dropdown-toggle {
  min-height: 2.6rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--vr-line-strong);
  border-radius: 0.4rem;
  background: #070b16;
}

.vr-select ::v-deep .vs__selected-options .vs__selected,
.vr-select ::v-deep .vs__selected {
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
  color: var(--vr-text) !important;
  font-family: var(--vr-mono) !important;
  font-size: 0.98rem !important;
}

.vr-select ::v-deep .vs__search,
.vr-select ::v-deep .vs__search::placeholder {
  color: var(--vr-muted);
  font-family: var(--vr-mono);
  font-size: 0.98rem;
}

.vr-select ::v-deep .vs__dropdown-option {
  padding: 0.5rem 0.75rem;
  font-family: var(--vr-mono);
  font-size: 0.95rem;
  white-space: normal;
}

.vr-answer.is-correct .vr-select ::v-deep .vs__dropdown-toggle {
  border-color: var(--vr-green);
}

.vr-answer.is-wrong .vr-select ::v-deep .vs__dropdown-toggle {
  border-color: var(--vr-red);
}

.vr-result {
  margin: 0.75rem 0 0;
  font-weight: 600;

  &.is-correct {
    color: var(--vr-green);
  }

  &.is-wrong {
    color: var(--vr-red);
  }
}

.vr-incident {
  padding: 1.25rem 1.5rem 1.5rem;
  color: var(--vr-text);
}

.vr-incident-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--vr-line);

  span:first-child {
    color: var(--vr-muted);
  }
}

.vr-incident-sev {
  color: var(--vr-red);
  font-weight: 600;
}

.vr-incident-message {
  margin-top: 1rem;
  line-height: 1.6;

  p {
    margin: 0;
  }

  p + p {
    margin-top: 0.75rem;
  }
}

@media (max-width: 1100px) {
  .vr-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
